import type { PropertyType } from '@nexofacil/schemas';
import { useState } from 'react';
import type { PublicPropertySummary } from '@/lib/api';
import { propertyTypeLabel, propertyTypePlural } from '@/lib/property-display';
import { cn } from '@/lib/utils';

// Abaixo disso a lista cabe de olho; filtro só atrapalha.
const MIN_FOR_FILTERS = 4;

/** "São José " e "sao jose" são o mesmo bairro. */
const hoodKey = (s: string) =>
  s.normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();

/**
 * Filtro por tipo e bairro das landings. Cada dimensão só aparece quando
 * tem pelo menos 2 opções de verdade na lista do corretor.
 */
export function usePropertyFilters(properties: PublicPropertySummary[]) {
  const [type, setType] = useState<PropertyType | null>(null);
  const [hood, setHood] = useState<string | null>(null);

  const types = (Object.keys(propertyTypeLabel) as PropertyType[]).filter((t) =>
    properties.some((p) => p.propertyType === t),
  );
  const hoods = new Map<string, string>();
  for (const p of properties) {
    if (!p.neighborhood) continue;
    const key = hoodKey(p.neighborhood);
    const label = p.neighborhood.trim();
    // Entre "centro" e "Centro", mostra o que começa com maiúscula.
    const seen = hoods.get(key);
    const capitalized = (x: string) => x.charAt(0) !== x.charAt(0).toLowerCase();
    if (!seen || (!capitalized(seen) && capitalized(label))) {
      hoods.set(key, label);
    }
  }

  const enabled = properties.length >= MIN_FOR_FILTERS;
  const filtered = properties.filter(
    (p) =>
      (!type || p.propertyType === type) &&
      (!hood || (p.neighborhood && hoodKey(p.neighborhood) === hood)),
  );

  return {
    filtered,
    filters: {
      types: enabled && types.length >= 2 ? types : [],
      hoods: enabled && hoods.size >= 2
        ? [...hoods].sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
        : [],
      type,
      setType,
      hood,
      setHood,
    },
  };
}

type Filters = ReturnType<typeof usePropertyFilters>['filters'];

export function PropertyFilterBar({
  filters,
  accent,
  className,
}: {
  filters: Filters;
  /** Cor do chip ativo no mundo da landing (hex). Sem ela, usa o primary do tema. */
  accent?: string;
  className?: string;
}) {
  const { types, hoods, type, setType, hood, setHood } = filters;
  if (types.length === 0 && hoods.length === 0) return null;

  const chip = (active: boolean) =>
    cn(
      'h-10 rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      active
        ? accent
          ? 'text-white'
          : 'border-primary bg-primary text-primary-foreground'
        : 'border-black/15 bg-white/70 hover:bg-white',
    );
  const activeStyle = (active: boolean) =>
    active && accent ? { backgroundColor: accent, borderColor: accent } : undefined;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {types.length > 0 && (
        <div role="group" aria-label="Tipo de imóvel" className="flex flex-wrap gap-2">
          <button
            type="button"
            aria-pressed={type === null}
            onClick={() => setType(null)}
            className={chip(type === null)}
            style={activeStyle(type === null)}
          >
            Todos
          </button>
          {types.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={type === t}
              onClick={() => setType(type === t ? null : t)}
              className={chip(type === t)}
              style={activeStyle(type === t)}
            >
              {propertyTypePlural[t]}
            </button>
          ))}
        </div>
      )}
      {hoods.length > 0 && (
        <select
          aria-label="Bairro"
          value={hood ?? ''}
          onChange={(e) => setHood(e.target.value || null)}
          className="h-10 rounded-full border border-black/15 bg-white/70 px-4 text-sm font-medium"
        >
          <option value="">Todos os bairros</option>
          {hoods.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/** Mensagem quando o filtro zera a lista. */
export function NoFilterMatch({ filters }: { filters: Filters }) {
  return (
    <p className="py-10 text-center text-base">
      Nenhum imóvel com esse filtro.{' '}
      <button
        type="button"
        onClick={() => {
          filters.setType(null);
          filters.setHood(null);
        }}
        className="font-semibold underline underline-offset-4"
      >
        Ver todos
      </button>
    </p>
  );
}
